import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getChallans, getChallanSummary, getChallansCsvUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/utils';
import { Plus, MagnifyingGlass, Eye, Truck, ArrowsClockwise, Funnel, CalendarBlank, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';

const statusConfig = {
    draft: { label: 'Draft', variant: 'secondary' },
    issued: { label: 'Issued', variant: 'default' },
    closed: { label: 'Closed', variant: 'outline' },
    cancelled: { label: 'Cancelled', variant: 'destructive' },
};

const TYPES = ['Outward', 'Inward', 'Return', 'Job Work', 'Transfer', 'Sample'];
const DEST_TYPES = ['Fabricator', 'Customer', 'Warehouse', 'Vendor', 'Office', 'Transporter'];

export default function ChallanList() {
    const { can } = useAuth();
    const [challans, setChallans] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [destFilter, setDestFilter] = useState('');
    const [periodFilter, setPeriodFilter] = useState('');
    const [summary, setSummary] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.challan_type = typeFilter;
            if (destFilter) params.destination_type = destFilter;
            if (periodFilter) params.period = periodFilter;
            const res = await getChallans(params);
            setChallans(res.data.challans);
            setTotal(res.data.total);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [search, statusFilter, typeFilter, destFilter, periodFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        getChallanSummary().then(res => setSummary(res.data)).catch(() => {});
    }, []);

    return (
        <div className="space-y-6" data-testid="challan-list-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Delivery Challans</h1>
                    <p className="text-sm text-muted-foreground mt-1">Material movement & delivery tracking</p>
                </div>
                <div className="flex gap-2">
                    {can('export_challans') && (
                        <Button variant="outline" size="sm" onClick={() => window.open(getChallansCsvUrl(), '_blank')} data-testid="export-csv-btn">
                            <DownloadSimple size={16} className="mr-1" />Export CSV
                        </Button>
                    )}
                    {can('create_challans') && (
                        <Button asChild data-testid="new-challan-btn">
                            <Link to="/challans/new"><Plus size={18} className="mr-2" />New Challan</Link>
                        </Button>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="challan-summary">
                    <div className="bg-card border border-border rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold">{summary.total}</div>
                        <div className="text-[10px] text-muted-foreground uppercase">Total</div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold">{summary.this_week}</div>
                        <div className="text-[10px] text-muted-foreground uppercase">This Week</div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold">{summary.this_month}</div>
                        <div className="text-[10px] text-muted-foreground uppercase">This Month</div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-amber-600">{summary.pending_returnable}</div>
                        <div className="text-[10px] text-muted-foreground uppercase">Pending Returns</div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold">{summary.by_status?.issued || 0}</div>
                        <div className="text-[10px] text-muted-foreground uppercase">Issued</div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-xs">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search challan #, party..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="challan-search" />
                </div>
                <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[130px] h-9" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="issued">Issued</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[130px] h-9" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={destFilter} onValueChange={v => setDestFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[140px] h-9" data-testid="filter-dest"><SelectValue placeholder="Destination" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Destinations</SelectItem>
                        {DEST_TYPES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={periodFilter} onValueChange={v => setPeriodFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[130px] h-9" data-testid="filter-period"><SelectValue placeholder="Period" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="this_week">This Week</SelectItem>
                        <SelectItem value="this_month">This Month</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="bg-card border border-border rounded-lg">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Challan #</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Destination</TableHead>
                            <TableHead>Party</TableHead>
                            <TableHead>Return</TableHead>
                            <TableHead className="text-right">Items</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created By</TableHead>
                            <TableHead className="w-16"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={10}><Skeleton className="h-8" /></TableCell></TableRow>
                        )) : challans.length === 0 ? (
                            <TableRow><TableCell colSpan={10} className="h-24 text-center text-muted-foreground">
                                No challans found. Create your first delivery challan.
                            </TableCell></TableRow>
                        ) : challans.map(c => {
                            const sc = statusConfig[c.status] || {};
                            return (
                                <TableRow key={c.id} data-testid={`challan-row-${c.id}`}>
                                    <TableCell>
                                        <Link to={`/challans/${c.id}`} className="font-mono text-sm font-semibold hover:text-primary transition-colors" data-testid={`challan-link-${c.id}`}>
                                            {c.challan_number}
                                        </Link>
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{c.challan_date}</TableCell>
                                    <TableCell><Badge variant="outline" className="text-[10px]">{c.challan_type}</Badge></TableCell>
                                    <TableCell className="text-xs">{c.destination_type}</TableCell>
                                    <TableCell className="text-sm">{c.destination_party || '—'}</TableCell>
                                    <TableCell>
                                        <Badge variant={c.return_type === 'returnable' ? 'default' : 'secondary'} className="text-[10px]">
                                            {c.return_type === 'returnable' ? 'Returnable' : 'Non-ret.'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-sm">{c.line_count || 0}</TableCell>
                                    <TableCell><Badge variant={sc.variant} className="text-[10px]">{sc.label || c.status}</Badge></TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{c.created_by_name || '—'}</TableCell>
                                    <TableCell>
                                        <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                            <Link to={`/challans/${c.id}`}><Eye size={14} /></Link>
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
